import React, { useState, useEffect } from 'react';
import api from '../utils/api';

const REPORT_LABELS = {
  daily_summary: 'Daily Summary',
  weekly_summary: 'Weekly Summary',
  monthly_summary: 'Monthly Summary'
};

export default function Notifications() {
  const [reportTypes, setReportTypes] = useState([]);
  const [expandedReport, setExpandedReport] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [expandedAlert, setExpandedAlert] = useState(null);
  const [newAlert, setNewAlert] = useState({ metric: 'roas', operator: '<', threshold_value: '', label: '' });
  const [editingAlert, setEditingAlert] = useState(null);
  const [editForm, setEditForm] = useState({ metric: '', operator: '', threshold_value: '', label: '' });

  useEffect(() => { fetchAll(); }, []);

  async function fetchAll() {
    try {
      const [subRes, alertRes] = await Promise.all([
        api.get('/api/notifications/report-subscriptions'),
        api.get('/api/notifications/threshold-alerts')
      ]);
      setReportTypes(subRes.data.reportTypes);
      setAlerts(alertRes.data.alerts);
    } catch (err) { console.error(err); }
  }

  async function toggleReportSub(userId, reportType, field, value) {
    const rt = reportTypes.find(r => r.report_type === reportType);
    const recipient = rt?.recipients.find(r => r.user_id === userId);
    if (!recipient) return;

    const isRemove = field === 'remove';
    const payload = {
      user_id: userId,
      report_type: reportType,
      notify_inapp: isRemove ? 0 : (field === 'notify_inapp' ? (value ? 1 : 0) : (recipient.notify_inapp ? 1 : 0)),
      notify_email: isRemove ? 0 : (field === 'notify_email' ? (value ? 1 : 0) : (recipient.notify_email ? 1 : 0))
    };

    setReportTypes(prev => prev.map(r => {
      if (r.report_type !== reportType) return r;
      const newRecipients = r.recipients.map(rec => {
        if (rec.user_id !== userId) return rec;
        if (isRemove) return { ...rec, notify_inapp: 0, notify_email: 0 };
        return { ...rec, [field]: value ? 1 : 0 };
      });
      return { ...r, recipients: newRecipients, activeCount: newRecipients.filter(rec => rec.notify_inapp || rec.notify_email).length };
    }));

    await api.put('/api/notifications/report-subscriptions', payload);
  }

  async function toggleAlertSub(alertId, userId, field, value) {
    const alert = alerts.find(a => a.id === alertId);
    const recipient = alert?.recipients.find(r => r.user_id === userId);
    if (!recipient) return;

    const isRemove = field === 'remove';
    const payload = {
      alert_id: alertId,
      user_id: userId,
      notify_inapp: isRemove ? 0 : (field === 'notify_inapp' ? (value ? 1 : 0) : (recipient.notify_inapp ? 1 : 0)),
      notify_email: isRemove ? 0 : (field === 'notify_email' ? (value ? 1 : 0) : (recipient.notify_email ? 1 : 0))
    };

    setAlerts(prev => prev.map(a => {
      if (a.id !== alertId) return a;
      const newRecipients = a.recipients.map(rec => {
        if (rec.user_id !== userId) return rec;
        if (isRemove) return { ...rec, notify_inapp: 0, notify_email: 0 };
        return { ...rec, [field]: value ? 1 : 0 };
      });
      return { ...a, recipients: newRecipients, activeCount: newRecipients.filter(rec => rec.notify_inapp || rec.notify_email).length };
    }));

    await api.put('/api/notifications/alert-subscriptions', payload);
  }

  async function addAlert(e) {
    e.preventDefault();
    await api.post('/api/notifications/threshold-alerts', { ...newAlert, threshold_value: parseFloat(newAlert.threshold_value) });
    setNewAlert({ metric: 'roas', operator: '<', threshold_value: '', label: '' });
    fetchAll();
  }

  async function deleteAlert(id) {
    if (!window.confirm('Are you sure you want to remove this alert?')) return;
    await api.delete(`/api/notifications/threshold-alerts/${id}`);
    setAlerts(prev => prev.filter(a => a.id !== id));
    if (expandedAlert === id) setExpandedAlert(null);
  }

  async function toggleAlertEnabled(id, enabled) {
    await api.put(`/api/notifications/threshold-alerts/${id}`, { enabled: enabled ? 1 : 0 });
    setAlerts(prev => prev.map(a => a.id === id ? { ...a, enabled: enabled ? 1 : 0 } : a));
  }

  function startEditAlert(a) {
    setEditingAlert(a.id);
    setEditForm({ metric: a.metric, operator: a.operator, threshold_value: a.threshold_value, label: a.label || '' });
  }

  async function saveEditAlert(id) {
    await api.put(`/api/notifications/threshold-alerts/${id}`, {
      metric: editForm.metric, operator: editForm.operator,
      threshold_value: parseFloat(editForm.threshold_value), label: editForm.label
    });
    setEditingAlert(null);
    fetchAll();
  }

  function formatMetric(metric) {
    const map = { roas: 'ROAS', single_refund: 'Single Refund', gmv_below_avg_pct: 'GMV Below Avg %', netProfit: 'Net Profit', adSpend: 'Ad Spend' };
    return map[metric] || metric;
  }

  return (
    <div>
      <h2 className="page-title" style={{ marginBottom: '20px' }}>Notification Configuration</h2>

      <div className="home-grid">
      {/* Scheduled Reports */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Scheduled Reports</h3>
        </div>
        {reportTypes.map(rt => (
          <div key={rt.report_type} style={{ borderBottom: '1px solid var(--border)' }}>
            <div
              onClick={() => setExpandedReport(expandedReport === rt.report_type ? null : rt.report_type)}
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', cursor: 'pointer', userSelect: 'none' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '12px', color: 'var(--text-light)', transition: 'transform 0.15s', transform: expandedReport === rt.report_type ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                <span style={{ fontWeight: 600, fontSize: '14px' }}>{REPORT_LABELS[rt.report_type]}</span>
              </div>
              <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                {rt.activeCount} recipient{rt.activeCount !== 1 ? 's' : ''}
              </span>
            </div>
            {expandedReport === rt.report_type && (
              <RecipientTable recipients={rt.recipients} onToggle={(userId, field, value) => toggleReportSub(userId, rt.report_type, field, value)} />
            )}
          </div>
        ))}
      </div>

      {/* Threshold Alerts */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Threshold Alerts</h3>
        </div>
        {alerts.map(a => (
          <div key={a.id} style={{ borderBottom: '1px solid var(--border)' }}>
            <div
              onClick={() => editingAlert !== a.id && setExpandedAlert(expandedAlert === a.id ? null : a.id)}
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', cursor: editingAlert === a.id ? 'default' : 'pointer', userSelect: 'none' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '12px', color: 'var(--text-light)', transition: 'transform 0.15s', transform: expandedAlert === a.id ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
                <span style={{ fontWeight: 600, fontSize: '14px' }}>{a.label || formatMetric(a.metric)}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }} onClick={e => e.stopPropagation()}>
                <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                  {a.activeCount} recipient{a.activeCount !== 1 ? 's' : ''}
                </span>
                <div className="alert-actions">
                  <button className="btn btn-secondary btn-sm" onClick={() => startEditAlert(a)} style={{ padding: '2px 8px', fontSize: '11px' }}>Edit</button>
                  <button className="btn btn-danger btn-sm" onClick={() => deleteAlert(a.id)} style={{ padding: '2px 8px', fontSize: '11px' }}>Remove</button>
                </div>
              </div>
            </div>
            {editingAlert === a.id && (
              <div style={{ paddingBottom: '12px', paddingLeft: '20px', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div className="form-group" style={{ marginBottom: 0, minWidth: '80px', flex: 1 }}>
                  <label style={{ fontSize: '12px' }}>Label</label>
                  <input value={editForm.label} onChange={e => setEditForm({ ...editForm, label: e.target.value })} placeholder="Alert name"
                    style={{ padding: '5px 8px', fontSize: '13px' }} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label style={{ fontSize: '12px' }}>Metric</label>
                  <select value={editForm.metric} onChange={e => setEditForm({ ...editForm, metric: e.target.value })}
                    style={{ padding: '5px 8px', fontSize: '13px' }}>
                    <option value="roas">ROAS</option>
                    <option value="single_refund">Single Refund</option>
                    <option value="gmv_below_avg_pct">GMV Below Avg %</option>
                    <option value="netProfit">Net Profit</option>
                    <option value="adSpend">Ad Spend</option>
                  </select>
                </div>
                <div className="form-group" style={{ marginBottom: 0, maxWidth: '55px' }}>
                  <label style={{ fontSize: '12px' }}>Op</label>
                  <select value={editForm.operator} onChange={e => setEditForm({ ...editForm, operator: e.target.value })}
                    style={{ padding: '5px 8px', fontSize: '13px' }}>
                    <option value="<">&lt;</option><option value=">">&gt;</option>
                    <option value="<=">&lt;=</option><option value=">=">&gt;=</option>
                  </select>
                </div>
                <div className="form-group" style={{ marginBottom: 0, maxWidth: '70px' }}>
                  <label style={{ fontSize: '12px' }}>Value</label>
                  <input type="number" step="any" value={editForm.threshold_value} onChange={e => setEditForm({ ...editForm, threshold_value: e.target.value })}
                    style={{ padding: '5px 8px', fontSize: '13px' }} />
                </div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button className="btn btn-primary btn-sm" onClick={() => saveEditAlert(a.id)} style={{ padding: '5px 10px', fontSize: '12px' }}>Save</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setEditingAlert(null)} style={{ padding: '5px 10px', fontSize: '12px' }}>Cancel</button>
                </div>
              </div>
            )}
            {expandedAlert === a.id && editingAlert !== a.id && (
              <RecipientTable recipients={a.recipients} onToggle={(userId, field, value) => toggleAlertSub(a.id, userId, field, value)} />
            )}
          </div>
        ))}
        {alerts.length === 0 && (
          <p style={{ color: 'var(--text-light)', fontSize: '13px', padding: '8px 0' }}>No threshold alerts configured.</p>
        )}

        <form onSubmit={addAlert} style={{ display: 'flex', gap: '8px', marginTop: '16px', flexWrap: 'wrap', alignItems: 'flex-end', borderTop: '1px solid var(--border)', paddingTop: '16px' }}>
          <div className="form-group" style={{ marginBottom: 0, minWidth: '100px' }}>
            <label>Label</label>
            <input value={newAlert.label} onChange={e => setNewAlert({ ...newAlert, label: e.target.value })} placeholder="Alert name" />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Metric</label>
            <select value={newAlert.metric} onChange={e => setNewAlert({ ...newAlert, metric: e.target.value })}>
              <option value="roas">ROAS</option>
              <option value="single_refund">Single Refund Amount</option>
              <option value="gmv_below_avg_pct">GMV Below Avg %</option>
              <option value="netProfit">Net Profit</option>
              <option value="adSpend">Ad Spend</option>
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0, maxWidth: '60px' }}>
            <label>Op</label>
            <select value={newAlert.operator} onChange={e => setNewAlert({ ...newAlert, operator: e.target.value })}>
              <option value="<">&lt;</option><option value=">">&gt;</option>
              <option value="<=">&lt;=</option><option value=">=">&gt;=</option>
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0, maxWidth: '80px' }}>
            <label>Value</label>
            <input type="number" step="any" value={newAlert.threshold_value} onChange={e => setNewAlert({ ...newAlert, threshold_value: e.target.value })} required />
          </div>
          <button type="submit" className="btn btn-primary">Add Alert</button>
        </form>
      </div>
      </div>
    </div>
  );
}

function RecipientTable({ recipients, onToggle, onAdd }) {
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const active = recipients.filter(r => r.notify_inapp || r.notify_email);
  const inactive = recipients.filter(r => !r.notify_inapp && !r.notify_email);
  const filtered = search ? inactive.filter(r => r.name.toLowerCase().includes(search.toLowerCase()) || r.email.toLowerCase().includes(search.toLowerCase())) : [];

  function handleAdd(user) {
    onToggle(user.user_id, 'notify_inapp', true);
    setSearch('');
    setShowAdd(false);
  }

  return (
    <div style={{ paddingBottom: '12px', paddingLeft: '20px' }}>
      {active.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', fontSize: '12px', fontWeight: 600, padding: '4px 8px', color: 'var(--text-light)' }}>Name</th>
              <th style={{ textAlign: 'center', fontSize: '12px', fontWeight: 600, padding: '4px 8px', color: 'var(--text-light)', width: '60px' }}>In-App</th>
              <th style={{ textAlign: 'center', fontSize: '12px', fontWeight: 600, padding: '4px 8px', color: 'var(--text-light)', width: '60px' }}>Email</th>
              <th style={{ width: '30px' }}></th>
            </tr>
          </thead>
          <tbody>
            {active.map(r => (
              <tr key={r.user_id}>
                <td style={{ padding: '6px 8px', fontSize: '13px' }}>{r.name}</td>
                <td style={{ textAlign: 'center', padding: '6px 8px' }}>
                  <input type="checkbox" checked={!!r.notify_inapp} onChange={e => onToggle(r.user_id, 'notify_inapp', e.target.checked)} style={{ width: 'auto' }} />
                </td>
                <td style={{ textAlign: 'center', padding: '6px 8px' }}>
                  <input type="checkbox" checked={!!r.notify_email} onChange={e => onToggle(r.user_id, 'notify_email', e.target.checked)} style={{ width: 'auto' }} />
                </td>
                <td style={{ textAlign: 'center', padding: '6px 8px' }}>
                  <button onClick={() => { onToggle(r.user_id, 'remove', true); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-light)', fontSize: '14px', padding: 0 }} title="Remove">&times;</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {active.length === 0 && (
        <div style={{ fontSize: '13px', color: 'var(--text-light)', padding: '4px 8px' }}>No recipients</div>
      )}
      {showAdd ? (
        <div style={{ marginTop: '8px', position: 'relative' }}>
          <input type="text" placeholder="Search users..." value={search} onChange={e => setSearch(e.target.value)} autoFocus
            style={{ width: '100%', padding: '6px 10px', fontSize: '13px', border: '1px solid var(--border)', borderRadius: '6px' }} />
          {search && filtered.length > 0 && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid var(--border)', borderRadius: '6px', boxShadow: 'var(--shadow-lg)', maxHeight: '150px', overflowY: 'auto', zIndex: 50 }}>
              {filtered.map(u => (
                <div key={u.user_id} onClick={() => handleAdd(u)}
                  style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '13px', borderBottom: '1px solid var(--border)' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'white'; }}>
                  <span style={{ fontWeight: 500 }}>{u.name}</span>
                  <span style={{ color: 'var(--text-light)', marginLeft: '6px', fontSize: '12px' }}>{u.email}</span>
                </div>
              ))}
            </div>
          )}
          {search && filtered.length === 0 && (
            <div style={{ fontSize: '12px', color: 'var(--text-light)', marginTop: '4px' }}>No users found</div>
          )}
          <button onClick={() => { setShowAdd(false); setSearch(''); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-light)', fontSize: '12px', marginTop: '4px' }}>Cancel</button>
        </div>
      ) : (
        <button onClick={() => setShowAdd(true)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary)', fontSize: '13px', padding: '6px 8px', marginTop: '4px' }}>
          + Add Recipient
        </button>
      )}
    </div>
  );
}
